<div class="vm-root">

  <!-- ══════════════════════════════════════════════════════════════════
       Pre-connection loading overlay
  ══════════════════════════════════════════════════════════════════ -->
  <div class="vm-overlay" *ngIf="isPerformingPreConnectionSync">
    <mat-card class="overlay-card" appearance="outlined">
      <mat-card-content class="overlay-loading-content">
        <mat-spinner diameter="48" color="primary"></mat-spinner>
        <h3 class="overlay-title">Preparing VM…</h3>
        <mat-progress-bar mode="indeterminate" color="primary"></mat-progress-bar>
      </mat-card-content>
    </mat-card>
  </div>

  <!-- ══════════════════════════════════════════════════════════════════
       Connection error overlay
  ══════════════════════════════════════════════════════════════════ -->
  <div class="vm-overlay vm-overlay--scrim"
    *ngIf="connectionError && !isConnected && !isDuplicateSession && !isPerformingPreConnectionSync && !isConnecting">
    <mat-card class="overlay-card overlay-card--error" [ngClass]="'overlay-card--' + errorCode">
      <mat-card-header>
        <div mat-card-avatar class="error-avatar" [ngClass]="'error-avatar--' + errorCode">
          <mat-icon class="error-avatar-icon">{{ errorIcon }}</mat-icon>
        </div>
        <mat-card-title>{{ errorTitle }}</mat-card-title>
        <mat-card-subtitle>{{ errorHint }}</mat-card-subtitle>
      </mat-card-header>
      <mat-card-content>
        <p class="overlay-error-body">{{ connectionError }}</p>
      </mat-card-content>
      <mat-card-actions align="end">
        <button mat-raised-button color="warn" (click)="closeCurrentTab()">
          <mat-icon>close</mat-icon>&nbsp;Close This Tab
        </button>
      </mat-card-actions>
    </mat-card>
  </div>

  <!-- ══════════════════════════════════════════════════════════════════
       Duplicate session overlay
  ══════════════════════════════════════════════════════════════════ -->
  <div class="vm-overlay vm-overlay--scrim vm-overlay--duplicate" *ngIf="isDuplicateSession">
    <mat-card class="overlay-card overlay-card--duplicate">
      <mat-card-header>
        <div mat-card-avatar class="error-avatar error-avatar--duplicate">
          <mat-icon class="error-avatar-icon">warning_amber</mat-icon>
        </div>
        <mat-card-title>Session Already Active</mat-card-title>
        <mat-card-subtitle>Another session is open for your account.</mat-card-subtitle>
      </mat-card-header>
      <mat-card-content>
        <p class="overlay-error-body">{{ duplicateSessionError }}</p>
      </mat-card-content>
      <mat-card-actions align="end">
        <button mat-raised-button color="warn" (click)="closeCurrentTab()">
          <mat-icon>close</mat-icon>&nbsp;Close This Tab (auto closes in {{ autoCloseCountdown }}s)
        </button>
      </mat-card-actions>
    </mat-card>
  </div>

  <!-- ══════════════════════════════════════════════════════════════════
       Main layout: sidenav container + VM display
  ══════════════════════════════════════════════════════════════════ -->
  <mat-sidenav-container class="vm-layout" [hasBackdrop]="true">

    <!-- VM content area -->
    <mat-sidenav-content class="vm-content">

      <!-- Guacamole render target — id/template-ref MUST NOT be renamed -->
      <div class="vm-display" [class.visible]="isConnected && !isDuplicateSession">
        <div id="display" #displayContainer></div>
      </div>

      <!-- Connecting spinner (inner overlay) -->
      <div class="vm-inner-overlay" *ngIf="isConnecting || (isConnected && !isContentReady && !isReconnecting)">
        <mat-spinner diameter="56" color="primary"></mat-spinner>
        <p class="connecting-label">Connecting to VM…</p>
      </div>

      <!-- Reconnecting banner (top strip) -->
      <div class="reconnecting-banner" *ngIf="isReconnecting && !isDuplicateSession">
        <mat-icon class="spin-icon">sync</mat-icon>
        <div class="reconnecting-text">
          <strong>Connection Interrupted</strong>
          <span>Reconnecting… (attempt {{ reconnectAttemptDisplay }}&thinsp;/&thinsp;{{ reconnectMaxAttempts }})</span>
        </div>
        <mat-progress-bar mode="indeterminate" color="accent" class="reconnecting-bar"></mat-progress-bar>
      </div>

      <!-- Monitoring chip (shown to participants being observed) -->
      <div class="monitoring-chip" *ngIf="isBeingMonitored">
        <mat-icon>visibility</mat-icon>
        <span>Your instructor is monitoring this session</span>
      </div>

    </mat-sidenav-content>

    <!-- ══ Details drawer (right side, overlay mode) ═════════════════ -->
    <mat-sidenav class="vm-details-drawer" mode="over" position="end"
      [opened]="sidebarVisible && isConnected && !isDuplicateSession" (openedChange)="onDrawerOpenedChange($event)">

      <!-- Drawer toolbar -->
      <div class="drawer-toolbar">
        <mat-icon class="drawer-toolbar-icon">computer</mat-icon>
        <span class="drawer-toolbar-title">VM Details</span>
        <button mat-icon-button matTooltip="Close drawer" (click)="toggleSidebar()">
          <mat-icon>chevron_right</mat-icon>
        </button>
      </div>

      <mat-divider></mat-divider>

      <div class="drawer-body">

        <!-- ── Disconnect / Reconnect ─────────────────────────── -->
        <section class="drawer-section">
          <div class="drawer-action-row">
            <button mat-stroked-button color="warn" class="drawer-action-row__btn" (click)="disconnect()"
              matTooltip="Disconnect and close this tab">
              <mat-icon>power_settings_new</mat-icon>&nbsp;Disconnect
            </button>
            <button mat-stroked-button color="primary" class="drawer-action-row__btn" (click)="drawerReconnect()"
              matTooltip="Close the current session and reconnect">
              <mat-icon>refresh</mat-icon>&nbsp;Reconnect
            </button>
          </div>
          <!-- Fullscreen and Clipboard re-permission sit side-by-side.
               The clipboard button is hidden when the browser has already
               granted clipboard-read access (no need to re-prompt). -->
          <div class="drawer-action-row">
            <button mat-stroked-button color="primary" class="drawer-action-row__btn drawer-fullscreen-btn"
              (click)="toggleFullscreen()" [matTooltip]="isFullscreen ? 'Exit full screen' : 'Enter full screen'">
              <mat-icon>{{ isFullscreen ? 'fullscreen_exit' : 'fullscreen' }}</mat-icon>
              &nbsp;{{ isFullscreen ? 'Exit Full Screen' : 'Full Screen' }}
            </button>

            <button *ngIf="isClipboardRelevant && clipboardPermissionState !== 'granted'" mat-stroked-button
              [color]="clipboardPermissionState === 'denied' ? 'warn' : 'primary'" class="drawer-action-row__btn"
              [disabled]="isRequestingClipboardPermission" (click)="requestClipboardPermission()" [matTooltip]="clipboardPermissionState === 'denied'
                      ? 'Clipboard access is blocked. Click to retry — if it fails, reset permissions via the browser\'s site settings (lock icon in address bar).'
                      : 'Re-prompt the browser for clipboard access so copy/paste shortcuts work'">
              <mat-icon>{{ clipboardPermissionState === 'denied' ? 'content_paste_off' : 'content_paste_go'
                }}</mat-icon>
              &nbsp;{{ isRequestingClipboardPermission ? 'Requesting…' : (clipboardPermissionState === 'denied' ?
              'Clipboard Blocked' : 'Clipboard Access') }}
            </button>
          </div>

          <!-- Session recording toggle — trainer/admin contexts only -->
          <div *ngIf="showRecordingToggle" class="drawer-recording-toggle-row">
            <mat-icon class="drawer-recording-toggle-row__icon">fiber_manual_record</mat-icon>
            <span class="drawer-recording-toggle-row__label">Session Recording</span>
            <mat-slide-toggle class="drawer-recording-toggle-row__toggle" color="warn"
              [(ngModel)]="recordingOverrideEnabled"
              matTooltip="Recording is off by default for trainer and template sessions. Please enable if explicitly needed to record."
              aria-label="Enable session recording">
            </mat-slide-toggle>
          </div>
        </section>

        <mat-divider></mat-divider>

        <!-- ── Account credentials ──────────────────────────────── -->
        <section class="drawer-section">
          <p class="drawer-section-label">
            <mat-icon class="section-icon">manage_accounts</mat-icon>
            VM Account Credentials
            <button mat-icon-button class="section-info-btn"
              matTooltip="Credentials used to log into the VM desktop. Use ↵ to type them directly into the VM, or copy them to paste manually."
              aria-label="VM Account Credentials info">
              <mat-icon>info_outline</mat-icon>
            </button>
          </p>

          <!-- Username -->
          <mat-form-field appearance="outline" class="drawer-field">
            <mat-label>Username</mat-label>
            <input matInput readonly [value]="connectionForm.username">
            <button matSuffix mat-icon-button [disabled]="isSendingKeystrokes" matTooltip="Type username into VM"
              (click)="sendUsernameKeystrokes()">
              <mat-icon>keyboard_return</mat-icon>
            </button>
            <button matSuffix mat-icon-button matTooltip="Copy username"
              (click)="copyToClipboard(connectionForm.username)">
              <mat-icon>content_copy</mat-icon>
            </button>
          </mat-form-field>

          <!-- Password -->
          <mat-form-field appearance="outline" class="drawer-field">
            <mat-label>Password</mat-label>
            <input matInput readonly [value]="connectionForm.password"
              [type]="vmPasswordRevealed ? 'text' : 'password'">
            <button matSuffix mat-icon-button (click)="toggleVmPassword()"
              [matTooltip]="vmPasswordRevealed ? 'Hide password' : 'Show password'"
              [attr.aria-label]="vmPasswordRevealed ? 'Hide password' : 'Reveal password'">
              <mat-icon>{{ vmPasswordRevealed ? 'visibility_off' : 'visibility' }}</mat-icon>
            </button>
            <button matSuffix mat-icon-button [disabled]="isSendingKeystrokes" matTooltip="Type password into VM"
              (click)="sendPasswordKeystrokes()">
              <mat-icon>keyboard_return</mat-icon>
            </button>
            <button matSuffix mat-icon-button matTooltip="Copy password"
              (click)="copyToClipboard(connectionForm.password)">
              <mat-icon>content_copy</mat-icon>
            </button>
          </mat-form-field>
        </section>

        <!-- ── AAD Credentials (context-gated: hidden for org-template-vm) ─── -->
        <ng-container *ngIf="!isOrgTemplateContext">

          <!-- Provisioning card: shown when UA template exists but no active credentials -->
          <app-vm-aad-status-card *ngIf="!hasAadCredentials" [classId]="aadClassId" [participantId]="aadParticipantId"
            [uaTemplateId]="aadUaTemplateId" [launchContext]="aadLaunchContext"
            (credentialsReady)="onAadCredentialsReady($event)">
          </app-vm-aad-status-card>

          <!-- Active credentials block — uses same mat-form-field styling as VM Account Credentials -->
          <ng-container *ngIf="hasAadCredentials">
            <mat-divider></mat-divider>
            <section class="drawer-section">
              <p class="drawer-section-label">
                <mat-icon class="section-icon">manage_accounts</mat-icon>
                Azure AD Credentials
                <button mat-icon-button class="section-info-btn"
                  matTooltip="Azure AD credentials for signing into Microsoft services on this VM. The Temporary Access Pass (TAP) is single-use and time-limited — regenerate it if it has expired."
                  aria-label="Azure AD Credentials info">
                  <mat-icon>info_outline</mat-icon>
                </button>
              </p>

              <!-- UPN field — styled like VM username -->
              <mat-form-field appearance="outline" class="drawer-field">
                <mat-label>Username (UPN)</mat-label>
                <input matInput readonly [value]="aadUpnLocalPart" [matTooltip]="aadUserPrincipalName">
                <button matSuffix mat-icon-button [disabled]="isSendingKeystrokes" matTooltip="Type UPN into VM"
                  (click)="sendAadUpnKeystrokes()" aria-label="Type UPN into VM">
                  <mat-icon>keyboard_return</mat-icon>
                </button>
                <button matSuffix mat-icon-button (click)="copyAadUpn()" matTooltip="Copy UPN" aria-label="Copy UPN">
                  <mat-icon>content_copy</mat-icon>
                </button>
              </mat-form-field>

              <!-- Temporary Access Pass — styled like VM password [regen][eye][send][copy] -->
              <mat-form-field appearance="outline" class="drawer-field">
                <mat-label>Temporary Access Pass</mat-label>
                <input matInput readonly [value]="aadTemporaryAccessPass" [type]="aadTapRevealed ? 'text' : 'password'">
                <button matSuffix mat-icon-button [disabled]="isRegeneratingTap" (click)="regenerateTap()"
                  matTooltip="Regenerate TAP" aria-label="Regenerate TAP">
                  <mat-spinner *ngIf="isRegeneratingTap" diameter="18"></mat-spinner>
                  <mat-icon *ngIf="!isRegeneratingTap">refresh</mat-icon>
                </button>
                <button matSuffix mat-icon-button (click)="toggleAadTap()"
                  [matTooltip]="aadTapRevealed ? 'Hide TAP' : 'Show TAP'"
                  [attr.aria-label]="aadTapRevealed ? 'Hide TAP' : 'Reveal TAP'">
                  <mat-icon>{{ aadTapRevealed ? 'visibility_off' : 'visibility' }}</mat-icon>
                </button>
                <button matSuffix mat-icon-button [disabled]="isSendingKeystrokes" matTooltip="Type TAP into VM"
                  (click)="sendAadTapKeystrokes()" aria-label="Type TAP into VM">
                  <mat-icon>keyboard_return</mat-icon>
                </button>
                <button matSuffix mat-icon-button (click)="copyAadTap()" matTooltip="Copy TAP" aria-label="Copy TAP">
                  <mat-icon>content_copy</mat-icon>
                </button>
              </mat-form-field>

              <!-- Azure Portal link field [open_in_new][send][copy] -->
              <mat-form-field appearance="outline" class="drawer-field">
                <mat-label>Azure Portal</mat-label>
                <input matInput readonly [value]="AZURE_PORTAL_URL">
                <button matSuffix mat-icon-button (click)="openPortalInNewTab()"
                  matTooltip="Open Azure portal in new tab" aria-label="Open Azure portal in new tab">
                  <mat-icon>open_in_new</mat-icon>
                </button>
                <button matSuffix mat-icon-button [disabled]="isSendingKeystrokes" (click)="sendPortalLinkKeystrokes()"
                  [matTooltip]="'Type \'' + AZURE_PORTAL_URL + '\' to VM'"
                  [attr.aria-label]="'Type ' + AZURE_PORTAL_URL + ' to VM'">
                  <mat-icon>keyboard_return</mat-icon>
                </button>
                <button matSuffix mat-icon-button (click)="copyToClipboard(AZURE_PORTAL_URL)" matTooltip="Copy URL"
                  aria-label="Copy portal URL">
                  <mat-icon>content_copy</mat-icon>
                </button>
              </mat-form-field>
            </section>
          </ng-container>

        </ng-container>

        <mat-divider></mat-divider>

        <!-- ── Special keys ─────────────────────────────────────── -->
        <section class="drawer-section">
          <p class="drawer-section-label">
            <mat-icon class="section-icon">keyboard_alt</mat-icon>
            Special Keys
            <button mat-icon-button class="section-info-btn"
              matTooltip="Send keyboard shortcuts and special keys directly to the VM. Hold Enter, Space, or Backspace to repeat."
              aria-label="Special Keys info">
              <mat-icon>info_outline</mat-icon>
            </button>
          </p>

          <div class="special-keys-table">

            <!-- Row 1: Ctrl + Alt + Del (full width) -->
            <div class="sk-row">
              <button class="sk-cell sk-cell--wide" [disabled]="isSendingKeystrokes" (click)="sendCtrlAltDel()"
                matTooltip="Ctrl+Alt+Del — unlock screen / task manager">
                Ctrl&nbsp;+&nbsp;Alt&nbsp;+&nbsp;Del
              </button>
            </div>

            <!-- Row 2: Esc | Tab | Enter -->
            <div class="sk-row">
              <button class="sk-cell" [disabled]="isSendingKeystrokes" (click)="sendEscapeKey()"
                matTooltip="Escape — cancel / close dialogs">
                Esc
              </button>
              <button class="sk-cell" [disabled]="isSendingKeystrokes" (click)="sendTabKey()"
                matTooltip="Tab — move focus / indent">
                Tab&nbsp;⇥
              </button>
              <button class="sk-cell" [disabled]="isSendingKeystrokes" [class.is-holding]="heldKey === 'enter'"
                (mousedown)="startHoldingKey('enter')" (mouseup)="stopHoldingKey()" (mouseleave)="stopHoldingKey()"
                matTooltip="Enter — hold to repeat">
                ↵&nbsp;Enter
              </button>
            </div>

            <!-- Row 3: Space | Backspace -->
            <div class="sk-row">
              <button class="sk-cell" [disabled]="isSendingKeystrokes" [class.is-holding]="heldKey === 'space'"
                (mousedown)="startHoldingKey('space')" (mouseup)="stopHoldingKey()" (mouseleave)="stopHoldingKey()"
                matTooltip="Space — hold to repeat">
                Space
              </button>
              <button class="sk-cell" [disabled]="isSendingKeystrokes" [class.is-holding]="heldKey === 'backspace'"
                (mousedown)="startHoldingKey('backspace')" (mouseup)="stopHoldingKey()" (mouseleave)="stopHoldingKey()"
                matTooltip="Backspace — hold to repeat">
                ⌫&nbsp;Bksp
              </button>
            </div>

          </div><!-- /special-keys-table -->
        </section>

        <mat-divider></mat-divider>

        <!-- ── File Transfer ────────────────────────────────────── -->
        <ng-container *ngIf="isFileTransferAllowed">
          <mat-divider></mat-divider>
          <section class="drawer-section">
            <p class="drawer-section-label">
              <mat-icon class="section-icon">folder_shared</mat-icon>
              File Transfer
              <button mat-icon-button class="section-info-btn"
                matTooltip="Upload (browser → VM): use the button below or drag files onto the VM screen. Files appear in the FromBrowser folder on the Desktop.&#10;&#10;Download (VM → browser): move files to the ToBrowser folder on the Desktop — they will be sent to your browser automatically within seconds."
                matTooltipClass="drawer-info-tooltip" aria-label="File Transfer info">
                <mat-icon>info_outline</mat-icon>
              </button>
            </p>

            <!-- Hidden file input — triggered by the button below -->
            <input type="file" multiple style="display:none" #fileInput (change)="onFileInputChange($event)" />

            <!-- Upload button -->
            <button mat-stroked-button color="primary" class="drawer-action-row__btn" style="width:100%"
              (click)="fileInput.click()" matTooltip="Select one or more files from your computer to send to the VM">
              <mat-icon>upload_file</mat-icon>&nbsp;Upload File to VM
            </button>


          </section>
        </ng-container>

      </div><!-- /drawer-body -->

      <!-- ── Drawer footer (outside scroll area) ────────────────────── -->
      <div class="drawer-footer">

        <!-- VM capabilities popover — opens on hover above footer -->
        <div class="vm-caps-popover" *ngIf="capsPopoverOpen" role="tooltip" aria-label="VM Capabilities"
          (mouseenter)="openCapsPopover()" (mouseleave)="closeCapsPopoverDelayed()">
          <div class="vm-caps-popover__header">
            <mat-icon class="vm-caps-popover__header-icon">tune</mat-icon>
            <span>VM Capabilities</span>
          </div>
          <div class="vm-caps-popover__body">
            <!-- Two-column table: Allowed | Not Allowed -->
            <div *ngIf="hasCapabilitiesAttached">
              <div class="vm-caps-table">
                <div class="vm-caps-col">
                  <div class="vm-caps-col-header vm-caps-col-header--allowed">
                    <mat-icon>check_circle</mat-icon> Allowed
                  </div>
                  <ng-container *ngIf="vmCapsAllowed.length > 0; else noAllowed">
                    <div class="vm-caps-row" *ngFor="let cap of vmCapsAllowed">
                      <mat-icon class="vm-caps-row__icon vm-caps-row__icon--allowed">{{ cap.icon }}</mat-icon>
                      <span>{{ cap.label }}</span>
                    </div>
                  </ng-container>
                  <ng-template #noAllowed>
                    <div class="vm-caps-row vm-caps-row--empty">None</div>
                  </ng-template>
                </div>
                <div class="vm-caps-divider"></div>
                <div class="vm-caps-col">
                  <div class="vm-caps-col-header vm-caps-col-header--denied">
                    <mat-icon>cancel</mat-icon> Not Allowed
                  </div>
                  <ng-container *ngIf="vmCapsDenied.length > 0; else noDenied">
                    <div class="vm-caps-row" *ngFor="let cap of vmCapsDenied">
                      <mat-icon class="vm-caps-row__icon vm-caps-row__icon--denied">{{ cap.icon }}</mat-icon>
                      <span>{{ cap.label }}</span>
                    </div>
                  </ng-container>
                  <ng-template #noDenied>
                    <div class="vm-caps-row vm-caps-row--empty">None</div>
                  </ng-template>
                </div>
              </div>
            </div>
          </div>
        </div>

        <!-- Footer action row -->
        <div class="drawer-footer-row">
          <!-- VM capabilities info button — hover opens popover -->
          <button *ngIf="hasCapabilitiesAttached" mat-raised-button class="drawer-footer-raised-btn"
            [class.active]="capsPopoverOpen" (mouseenter)="openCapsPopover()" (mouseleave)="closeCapsPopoverDelayed()"
            [attr.aria-expanded]="capsPopoverOpen" aria-label="VM capabilities">
            <mat-icon>info_outline</mat-icon>
            VM Capabilities
          </button>

          <!-- Spacer pushes collapse to the right -->
          <span class="drawer-footer-spacer"></span>

          <!-- Collapse drawer -->
          <button mat-raised-button class="drawer-footer-raised-btn" (click)="toggleSidebar()"
            [matTooltip]="isRecordingActive ? '⏺ This session is being recorded' : null" aria-label="Hide details">
            <mat-icon>chevron_right</mat-icon>
            Hide Details
            <span *ngIf="isRecordingActive" class="fab-screen-rec-dot"></span>
          </button>
        </div>
      </div><!-- /drawer-footer -->

    </mat-sidenav>

  </mat-sidenav-container>

  <!-- Floating drawer toggle (visible only when connected) -->
  <button mat-raised-button color="primary" class="vm-drawer-toggle" *ngIf="isConnected && !isDuplicateSession"
    [class.visible]="!sidebarVisible" [class.pulse]="!sidebarVisible" (click)="toggleSidebar()"
    [matTooltip]="isRecordingActive ? '⏺ This session is being recorded' : null">
    <mat-icon>computer</mat-icon>
    <span class="toggle-label">VM Details</span>
    <span *ngIf="isRecordingActive" class="fab-screen-rec-dot"></span>
  </button>

</div>
